import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearCheckIn,
  getDailyStatusSettings,
  getMyBoard,
  getReport,
  getTeamReport,
  getUserBoard,
  setCheckIn,
  type CheckInInput,
  type DailyStatusBoard,
  type TodayTask,
} from '../lib/dailyStatusApi';

export const MY_BOARD_KEY = ['daily-status', 'me'] as const;

/**
 * My today board. Polled every minute (like the reminders) so the check-in
 * banner appears on time and a second tab never shows stale check-ins.
 */
export function useMyDailyBoard(date?: string, enabled = true) {
  return useQuery({
    queryKey: [...MY_BOARD_KEY, date ?? 'today'],
    queryFn: () => getMyBoard(date),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useUserDailyBoard(userId: string | undefined, date?: string) {
  return useQuery({
    queryKey: ['daily-status', 'user', userId, date ?? 'today'],
    queryFn: () => getUserBoard(userId!, date),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

function applyTask(board: DailyStatusBoard, task: TodayTask): DailyStatusBoard {
  const tasks = board.tasks.map((t) => (t.id === task.id ? task : t));
  const checkedIn = tasks.filter((t) => t.checkIn !== null).length;
  return { ...board, tasks, pending: tasks.length - checkedIn, checkedIn };
}

/**
 * Set / clear a check-in with an optimistic board update, so the buttons
 * flip instantly and the comment never waits on the network. Everything the
 * check-in may have changed (task status, the summaries) is invalidated after.
 */
export function useSetCheckIn(date: string) {
  const qc = useQueryClient();
  const key = [...MY_BOARD_KEY, date];
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: CheckInInput | null }) =>
      input ? setCheckIn(taskId, date, input) : clearCheckIn(taskId, date).then(() => null),
    onMutate: async ({ taskId, input }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DailyStatusBoard>(key);
      if (previous) {
        const current = previous.tasks.find((t) => t.id === taskId);
        if (current) {
          const optimistic: TodayTask = {
            ...current,
            checkIn: input
              ? {
                  status: input.status,
                  taskStatusId: input.taskStatusId ?? null,
                  comment: input.comment ?? null,
                  commentId: current.checkIn?.commentId ?? null,
                  attachments: input.attachments ?? current.checkIn?.attachments ?? [],
                  updatedAt: new Date().toISOString(),
                }
              : null,
          };
          qc.setQueryData(key, applyTask(previous, optimistic));
        }
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSuccess: (task) => {
      if (task) {
        const board = qc.getQueryData<DailyStatusBoard>(key);
        if (board) qc.setQueryData(key, applyTask(board, task));
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['daily-summary'] });
      void qc.invalidateQueries({ queryKey: ['business-daily-summary'] });
      void qc.invalidateQueries({ queryKey: ['daily-status', 'report'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDailyReport(userId: string | undefined, date: string, enabled: boolean) {
  return useQuery({
    queryKey: ['daily-status', 'report', userId ?? 'me', date],
    queryFn: () => getReport(userId, date),
    enabled,
    staleTime: 30_000,
  });
}

export function useTeamDailyReport(date: string, enabled: boolean) {
  return useQuery({
    queryKey: ['daily-status', 'report', 'team', date],
    queryFn: () => getTeamReport(date),
    enabled,
    staleTime: 60_000,
  });
}

export const DAILY_STATUS_SETTINGS_KEY = ['settings', 'daily-status'] as const;

export function useDailyStatusSettings() {
  return useQuery({ queryKey: DAILY_STATUS_SETTINGS_KEY, queryFn: getDailyStatusSettings });
}
